import { notification } from 'antd';
import { useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { downloadFilesApi } from '@/integrations/index.api';
import { keysToSnakeCase } from '@/services';
import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';
import type { FileType, TDocumentsOnModeration } from '@/types/uploadFiles';

import useConfirmBitrix from '../../../hooks/useConfirmBitrix';
import type { FormType } from '../types/form-types';

interface IProps {
  methods: UseFormReturn<FormType>;
  files: Record<string, TFileAttached> | undefined;
  onSubmit: (values: Record<string, FileType>) => Promise<boolean>;
  handleClose: () => void;
  documentsOnModeration: TDocumentsOnModeration[];
}

const useSubmitAction = ({
  methods,
  files,
  onSubmit,
  handleClose,
  documentsOnModeration
}: IProps) => {
  const {
    selectedBank,
    isVehicleInfoValid,
    isBitrixDocumentsAccepted,
    isEuBankOfflineProcess
  } = useAppSelector((state) => state.financingStep);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [throttleEnabled, setThrottleEnabled] = useState(false);

  const { confirmBitrixLoading, handleConfirmBitrix } = useConfirmBitrix();

  const isSubmitDisabled =
    throttleEnabled ||
    Object.values(methods.formState.errors).length > 0 ||
    (selectedBank?.status.code === 'waiting_for_upload_files' &&
      !files?.pledgeAgreementTemplate) ||
    (!isBitrixDocumentsAccepted && !isEuBankOfflineProcess);

  const handleSubmit = async () => {
    if (
      selectedBank?.status.code === 'waiting_for_docs_confirmation' &&
      !isEuBankOfflineProcess
    ) {
      // Перенаправление на обработку по онлайн процессу
      handleConfirmBitrix();
      return;
    }

    if (
      !isVehicleInfoValid &&
      (selectedBank?.status.code === 'vehicle_registration' ||
        isEuBankOfflineProcess)
    ) {
      notification.error({
        title: 'Пожалуйста, заполните данные о транспортном средстве'
      });
      return;
    }

    const values = methods.getValues();

    const marriageDocuments = [
      'extrajudicialEnforcement',
      'marriageContract',
      'spouseConsent'
    ] as const;

    const isPersonMarried = Number(orderApp?.personalInfo.familyStatus) === 1;

    const isOnModeration = (documentType: string): boolean => {
      return documentsOnModeration.some(
        (doc) => doc.documentType === documentType
      );
    };

    if (!marriageDocuments.some((key) => isOnModeration(key))) {
      if (isPersonMarried) {
        if (!values.marriageContract && files?.marriageContract) {
          const fileResponse = await downloadFilesApi.downloadBankFile(
            files.marriageContract.uuid
          );
          methods.setValue('marriageContract', {
            base64: fileResponse.base64,
            name: fileResponse.name,
            size: 0
          });
        }

        if (!values.spouseConsent && files?.spouseConsent) {
          const fileResponse = await downloadFilesApi.downloadBankFile(
            files.spouseConsent.uuid
          );
          methods.setValue('extrajudicialEnforcement', {
            base64: fileResponse.base64,
            name: fileResponse.name,
            size: 0
          });
          methods.setValue('spouseConsent', {
            base64: fileResponse.base64,
            name: fileResponse.name,
            size: 0
          });
        }
      } else {
        if (!marriageDocuments.some((key) => values[key])) {
          // Без новых документов в форме, загружаем все текущие документы
          for (const key of marriageDocuments) {
            if (files?.[key]) {
              const fileResponse = await downloadFilesApi.downloadBankFile(
                files[key].uuid
              );
              for (const key_ of marriageDocuments) {
                methods.setValue(key_, {
                  base64: fileResponse.base64,
                  name: fileResponse.name,
                  size: 0
                });
              }
              break;
            }
          }
        } else if (!marriageDocuments.every((key) => values[key])) {
          // Есть новые документы в форме, дублируем их для других полей формы
          for (const key of marriageDocuments) {
            if (values[key]) {
              for (const key_ of marriageDocuments) {
                if (key_ === key || !values[key_]) continue;
                methods.setValue(key_, {
                  base64: values[key_].base64,
                  name: values[key_].name,
                  size: 0
                });
              }
              break;
            }
          }
        }
      }
    }

    const updatedValues = methods.getValues();

    if (isEuBankOfflineProcess) {
      for (const key of marriageDocuments) {
        if (updatedValues[key]) {
          methods.setValue(key, {
            ...updatedValues[key],
            reUpload: true
          });
        }
      }
    }

    const response = await onSubmit(
      keysToSnakeCase(methods.getValues()) as Record<string, FileType>
    );
    if (response) {
      handleClose();
      setThrottleEnabled(true);
      setTimeout(() => setThrottleEnabled(false), 10000);
    }
  };

  return {
    isSubmitDisabled,
    confirmBitrixLoading,
    handleSubmit
  };
};

export default useSubmitAction;
